
import React, { useState, useEffect } from "react";
import { FormFieldType } from "@/types/form";
import { useForm } from "../../../context/FormContext";
import { Textarea } from "@/components/ui/textarea";
import FieldLabel from "./FieldLabel";

interface TextareaFieldProps {
  element: FormFieldType;
}

export const TextareaField: React.FC<TextareaFieldProps> = ({ element }) => {
  const { formData, updateField, errors } = useForm();
  const fieldValue = formData[element.name || ""] as string || "";
  const error = element.name ? errors[element.name] : undefined;
  const [isDisabled, setIsDisabled] = useState(element.readOnly || false);
  
  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    if (!element.name) return;
    updateField(element.name, e.target.value);
  };
  
  // Check if this is one of the special fields that require minimum characters
  const isRatingBehaviorField = ["knowledge_behavior", "skills_behavior", "attitude_behavior"].includes(element.name || "");
  const charCount = fieldValue.length;
  const minChars = 50;
  const showCharCount = isRatingBehaviorField;
  
  // This effect makes the field respond to external changes to its disabled state
  useEffect(() => {
    const checkDisabledStatus = () => {
      const fieldElement = document.getElementById(element.id) as HTMLTextAreaElement;
      if (fieldElement) {
        setIsDisabled(fieldElement.disabled || fieldElement.readOnly || false);
      }
    };

    // Initial check
    checkDisabledStatus();

    // Set up a mutation observer to detect changes to the disabled attribute
    const observer = new MutationObserver(checkDisabledStatus);
    const fieldElement = document.getElementById(element.id);
    
    if (fieldElement) {
      observer.observe(fieldElement, { 
        attributes: true, 
        attributeFilter: ['disabled', 'readonly'] 
      });
    }

    return () => {
      observer.disconnect();
    };
  }, [element.id]);
  
  return (
    <div className="space-y-2">
      <FieldLabel element={element} />
      
      <Textarea
        id={element.id}
        name={element.name}
        placeholder={element.placeholder}
        value={fieldValue}
        onChange={handleChange}
        className={`min-h-[100px] ${((error && (charCount < minChars))) ? 'border-red-500' : ''} ${isDisabled ? 'bg-gray-100' : ''}`}
        disabled={isDisabled}
      />
      
      {showCharCount && (
        <div className="flex justify-between text-sm">
          <span className={charCount < minChars ? "text-red-500" : "text-green-500"}>
            {charCount} ตัวอักษร {charCount < minChars ? `(ต้องการอีก ${minChars - charCount} ตัวอักษร)` : "✓"}
          </span>
          <span className="text-gray-500">ขั้นต่ำ {minChars} ตัวอักษร</span>
        </div>
      )}
      
      {(error && (charCount < minChars)) && (
        <p className="text-sm text-red-500">{error}</p>
      )}
    </div>
  );
};

export default TextareaField;
